/**
 * Container Service
 * Handles container lookup, swap requests, and pickup requests with token management
 */

import { apiClient, ApiClient, ApiError } from './api';
import type { OrderDTO } from '../types/order';

/**
 * Container lookup parameters
 */
export interface ContainerLookupParams {
  searchValue: string; // Phone number (10 digits) or order number
}

/**
 * Container lookup response
 */
export interface ContainerLookupResponse {
  containers: OrderDTO[];
  lookupToken: string;
  expiresAt: string; // ISO date string
}

/**
 * Swap request parameters
 */
export interface SwapRequestParams {
  containerId: string;
  newMaterialType?: string;
  newContainerSize?: string;
  requestedDate: string; // ISO date string
  placementInstructions?: string;
}

/**
 * Pickup request parameters
 */
export interface PickupRequestParams {
  containerId: string;
  requestedDate: string; // ISO date string
  specialInstructions?: string;
}

/**
 * Token storage interface
 */
interface TokenData {
  token: string;
  expiresAt: Date;
}

/**
 * ContainerService class
 *
 * Manages container lookups and actions with secure token handling.
 * Tokens are stored in memory only for security.
 */
export class ContainerService {
  private client: ApiClient;
  private lookupToken: TokenData | null = null;

  constructor(client: ApiClient = apiClient) {
    this.client = client;
  }

  /**
   * Look up containers by phone number or order number
   *
   * @param params - Lookup parameters (phone or order number)
   * @returns Promise<ContainerLookupResponse> - Container data with lookup token
   * @throws {ApiError} - If lookup fails or rate limit exceeded
   */
  async lookup(params: ContainerLookupParams): Promise<ContainerLookupResponse> {
    const { searchValue } = params;

    // Validate search value
    if (!searchValue || searchValue.trim() === '') {
      throw new ApiError(
        'INVALID_INPUT',
        'Please enter a phone number or order number',
        400
      );
    }

    try {
      // Call backend API
      const response = await this.client.post<ContainerLookupResponse>(
        '/widget/containers/lookup',
        { searchValue: searchValue.trim() }
      );

      // Store token in memory
      if (response.lookupToken && response.expiresAt) {
        this.lookupToken = {
          token: response.lookupToken,
          expiresAt: new Date(response.expiresAt),
        };
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        // Re-throw API errors with better messages
        if (error.statusCode === 429) {
          throw new ApiError(
            'RATE_LIMIT_EXCEEDED',
            'Too many lookup attempts. Please wait a moment and try again.',
            429
          );
        }
        if (error.statusCode === 404) {
          throw new ApiError(
            'NOT_FOUND',
            'No container found. Please check your phone number or order number.',
            404
          );
        }
        throw error;
      }

      // Handle unexpected errors
      throw new ApiError(
        'LOOKUP_FAILED',
        'Failed to lookup container. Please try again.',
        500
      );
    }
  }

  /**
   * Request a container swap
   *
   * @param params - Swap request parameters
   * @returns Promise<void>
   * @throws {ApiError} - If swap request fails or token is invalid/expired
   */
  async requestSwap(params: SwapRequestParams): Promise<void> {
    this.validateToken();

    try {
      await this.client.post(
        '/widget/containers/swap',
        params,
        {
          headers: {
            'X-Lookup-Token': this.lookupToken!.token,
          },
        }
      );
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 401) {
        // Token expired or invalid
        this.clearToken();
        throw new ApiError(
          'TOKEN_EXPIRED',
          'Your session has expired. Please look up your container again.',
          401
        );
      }
      throw error;
    }
  }

  /**
   * Request a container pickup
   *
   * @param params - Pickup request parameters
   * @returns Promise<void>
   * @throws {ApiError} - If pickup request fails or token is invalid/expired
   */
  async requestPickup(params: PickupRequestParams): Promise<void> {
    this.validateToken();

    try {
      await this.client.post(
        '/widget/containers/pickup',
        params,
        {
          headers: {
            'X-Lookup-Token': this.lookupToken!.token,
          },
        }
      );
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 401) {
        // Token expired or invalid
        this.clearToken();
        throw new ApiError(
          'TOKEN_EXPIRED',
          'Your session has expired. Please look up your container again.',
          401
        );
      }
      throw error;
    }
  }

  /**
   * Check if the current token is valid
   *
   * @returns boolean - True if token exists and hasn't expired
   */
  isTokenValid(): boolean {
    if (!this.lookupToken) {
      return false;
    }

    // Check if token has expired (with 30 second buffer)
    const now = new Date();
    const expiryWithBuffer = new Date(this.lookupToken.expiresAt.getTime() - 30000);

    return now < expiryWithBuffer;
  }

  /**
   * Clear the stored token from memory
   */
  clearToken(): void {
    this.lookupToken = null;
  }

  /**
   * Validate token before making authenticated requests
   * @private
   * @throws {ApiError} - If token is missing or expired
   */
  private validateToken(): void {
    if (!this.isTokenValid()) {
      throw new ApiError(
        'TOKEN_REQUIRED',
        'Please look up your container first',
        401
      );
    }
  }
}

// Note: To use mock service in development, import from:
// import { containerService } from './services/__mocks__/container.service.mock'
// Otherwise, this exports the real service

// Export singleton instance
export const containerService = new ContainerService();
